import {
  FileUpload,
  PageHeader,
  PlusIcon,
  SetGoalModal,
  useQueryState,
} from '@devd-client/devd/components';
import { GoalContainer } from '../helpers/goals.styled';
import {
  Box,
  Button,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OrgTab } from '../components/GoalsTab/OrgTab';
import { useEffect, useState } from 'react';
import { ContributorTab } from '../components/GoalsTab/ContributorTab';
import { FaBullseye } from 'react-icons/fa';
import { TeamTab } from '../components/GoalsTab/TeamTab';
import { TbUpload } from 'react-icons/tb';
import { getOrgId, getRole } from '@devd-client/devd/auth';
import { UserRole } from '@devd-client/api';
import { GoalPageResponse } from '../helpers/goals.types';
interface TabData {
  tab: string;
  tabPannel?: React.ReactNode;
}

interface GoalsPageProps {
  data: GoalPageResponse;
}

export function GoalsPage({ data }: GoalsPageProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const setGoalPopup = useDisclosure();
  const [search, setSearch] = useQueryState('search');
  const [team, setTeam] = useQueryState('team');
  const [uploadFile, setUploadFile] = useState(false);
  const [uploadModalOpenedFor, setUploadOpenedFor] = useState('');

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      const tabIndex = tabData.findIndex(
        (tab) => tab.tab.toLowerCase() === tabFromUrl.toLowerCase()
      );
      if (tabIndex !== -1) {
        setActiveTab(tabIndex);
      }
    }
  }, [searchParams]);

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    setSearch('');
    setTeam('');
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabData[index].tab);
    setSearchParams(newSearchParams);
  };

  const tabData: TabData[] = [
    {
      tab: 'Organisation',
      tabPannel: <OrgTab team={team} setTeam={setTeam} />,
    },
    {
      tab: 'Team',
      tabPannel: <TeamTab team={team} setTeam={setTeam} />,
    },
    {
      tab: 'Contributor',
      tabPannel: (
        <ContributorTab
          search={search}
          setSearch={setSearch}
          team={team}
          setTeam={setTeam}
        />
      ),
    },
  ];

  return (
    <>
      {uploadFile ? (
        <FileUpload
          setUploadFile={setUploadFile}
          uploadLink={
            uploadModalOpenedFor === 'goals'
              ? `/v1/goals/org/${getOrgId()}/goals/import`
              : `/v1/goals/org/${getOrgId()}/goals/key-results/import`
          }
          queryKey={'goalsData'}
          uploadModalOpenedFor={uploadModalOpenedFor}
        />
      ) : (
        <GoalContainer>
          <PageHeader
            headingText="Goals"
            component={
              <Flex gap={2}>
                {(getRole() === UserRole.EMPLOYER_ADMIN ||
                  getRole() === UserRole.SUPPORT) && (
                  <>
                    <Tooltip
                      label="Set organization-wide color thresholds for goal progress: On Track (Green), Behind (Yellow), At Risk (Red). These defaults help standardize progress visualization."
                      hasArrow
                    >
                      <Button
                        variant="custom-outline"
                        size="sm"
                        onClick={setGoalPopup.onOpen}
                      >
                        <Flex align={'center'} gap={1}>
                          <FaBullseye />
                          Set Goal
                        </Flex>
                      </Button>
                    </Tooltip>
                    <Button
                      variant="custom-outline"
                      size="sm"
                      onClick={() => {
                        navigate('/goal/create');
                      }}
                    >
                      <Flex align={'center'} gap={1}>
                        <PlusIcon />
                        Create Goal
                      </Flex>
                    </Button>
                    <Button
                      variant="custom-outline"
                      size="sm"
                      onClick={() => {
                        setUploadFile(true);
                        setUploadOpenedFor('goals');
                      }}
                    >
                      <Flex align={'center'} gap={1}>
                        <TbUpload size={16} color="#4d4dff" />
                        Upload Goals
                      </Flex>
                    </Button>
                    <Button
                      variant="custom-outline"
                      size="sm"
                      onClick={() => {
                        setUploadFile(true);
                        setUploadOpenedFor('key-results');
                      }}
                    >
                      <Flex align={'center'} gap={1}>
                        <TbUpload size={16} color="#4d4dff" />
                        Upload Key Results
                      </Flex>
                    </Button>
                  </>
                )}
              </Flex>
            }
          />
          <Text
            fontFamily="heading"
            fontSize="sm"
            color="text.secondary"
            mb={'10px'}
          >
            {data?.data?.dto?.title}
          </Text>
          <Tabs mt={2} index={activeTab} onChange={handleTabChange}>
            <TabList>
              {tabData?.map((tab) => (
                <Tab
                  key={tab.tab}
                  fontSize={'sm'}
                  fontWeight="medium"
                  color="text.secondary"
                  _selected={{
                    color: 'primary',
                    borderBottom: '2px solid',
                    borderColor: 'primary',
                  }}
                >
                  {tab.tab}
                </Tab>
              ))}
            </TabList>

            <TabPanels>
              {tabData?.map((tab, index) => (
                <TabPanel key={tab.tab}>
                  {activeTab === index && tab.tabPannel}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
          {setGoalPopup.isOpen && (
            <SetGoalModal
              isOpen={setGoalPopup.isOpen}
              onClose={setGoalPopup.onClose}
              metricTitle="Goal Progress"
              queryKey="goalsData"
              metricName="GOAL_DASHBOARD"
              useUrlParams={false}
              hideTeamSelect={true}
              orgTeam="Organisation"
            />
          )}
        </GoalContainer>
      )}
    </>
  );
}

export default GoalsPage;
